'use client';

import React from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import Container from '../Container';

const testimonials = [
  {
    quote:
      "I use SEOZast to audit my blog's SEO; it's fast, accurate, and saves me hours.",
    name: 'Serafima Osovitny',
    source: 'Product Hunt',
    sourceUrl: 'https://www.producthunt.com/products/seozast/reviews',
    datePublished: '2025-06-24',
    avatar: '/images/avatars/serafima.jpg',
  },
  {
    quote: "Btw guys, I started using SEOZast to help me with my SEO and GEO. Because I'm a newbie in that domain and I didn't want to spend too much money on it. I got results pretty quickly and even learned a few things!",
    name: 'Vivien Mahe',
    source: 'X (Twitter)',
    sourceUrl: 'https://x.com/VivienMahe/status/1988924436129398803',
    datePublished: '2025-06-20',
    avatar: '/images/avatars/vivien.jpg',
  },
  {
    quote: "Just subscribed to seozast.com. Going to use it for my winery-hotels.com and konektly.app. Already have some traffic. Will see, how it will change in a month of using seozast.",
    name: 'Alexander',
    source: 'X (Twitter)',
    sourceUrl: 'https://x.com/alexanderOsso/status/1988291378460364896',
    datePublished: '2025-06-19',
    avatar: '/images/avatars/alexander.jpg',
    image: '/images/search-console.jpeg',
  },
  {
    quote: "Great product with a best price. You can't go wrong with this.",
    name: 'Nikita Y',
    source: 'Uneed',
    sourceUrl: 'https://www.uneed.best/tool/seozast?full=true&tab=comments',
    datePublished: '2025-06-24',
    avatar: '/images/avatars/nikita.jpg',
  },
  {
    quote:
      'Having worked on 17+ ventures, managing them through 8+ websites, SEOZast solved some of our SEO complexity using it as a quick insight alternative and also backed with other SEO tools for content creation.',
    name: 'Ajay Sahoo',
    source: 'Product Hunt',
    sourceUrl: 'https://www.producthunt.com/products/seozast/reviews',
    datePublished: '2025-06-01',
    avatar: null,
  },
  {
    quote: "Been following the development of SEOZast and really impressed with the concept. Super affordable and seems like a great fit for indie devs and solo founders. Love how it focuses on simplicity—AI blog writing, internal linking, and GitHub integration at this price point is rare.",
    name: 'Amanpreet Singh',
    source: 'Product Hunt',
    sourceUrl: 'https://www.producthunt.com/products/seozast/reviews',
    datePublished: '2025-06-23',
    avatar: null,
  },
  {
    quote:
      "This is an absolute game-changer for indie makers and content creators! SEOZast makes it insanely easy to spot what people are actually searching for, and the keyword clustering + autocomplete tools are super intuitive. Love how it cuts through the noise and helps me focus on topics that truly matter.",
    name: 'droid HZ',
    source: 'Fazier',
    sourceUrl: 'https://fazier.com/launches/seozast',
    datePublished: '2025-06-24',
    avatar: null,
  },
  {
    quote:
      "This is a breath of fresh air. I've been stuck between paying hundreds for bloated SEO suites or duct-taping free tools together. SEOZast is the lean, focused solution I've been waiting for. The AI-generated articles are surprisingly solid and a massive head start. But the real magic for me as a dev is the one-click publish to GitHub. Getting a fully formatted .tsx file that I can drop right into my Next.js project is just chef's kiss. For the price of a coffee, this is an absolute no-brainer for any indie hacker or startup that needs to get content out the door without the fluff.",
    name: 'Sophia Smith',
    source: 'Fazier',
    sourceUrl: 'https://fazier.com/launches/seozast',
    datePublished: '2025-06-25',
    avatar: null,
  },
  {
    quote: "SEOZast has helped me a ton managing SEO for our website. Its helped automate the process of blog writing and made things a lot easier for us.",
    name: 'Hadee',
    source: 'Direct Message',
    sourceUrl: null,
    datePublished: '2025-06-22',
    avatar: '/images/avatars/hadee.jpg',
  },
  {
    quote:
      "Thanks, now my seo doesn't cost more than my coffee. Love this",
    name: 'wealthsignalupdates',
    source: 'Fazier',
    sourceUrl: 'https://fazier.com/launches/seozast',
    datePublished: '2025-06-25',
    avatar: null,
  },
];

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null);
  const [isAnimating, setIsAnimating] = React.useState(false);

  const testimonialsPerView = 6; // 2 rows of 3
  const totalSlides = Math.ceil(testimonials.length / testimonialsPerView);
  const maxIndex = totalSlides - 1;

  const nextSlide = () => {
    if (!isAnimating && currentIndex < maxIndex) {
      setIsAnimating(true);
      setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const prevSlide = () => {
    if (!isAnimating && currentIndex > 0) {
      setIsAnimating(true);
      setCurrentIndex((prev) => Math.max(prev - 1, 0));
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'ArrowRight') nextSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, isAnimating]);

  return (
    <section className="bg-gray-50 py-16 border-b border-gray-200">
      <Container>
        <div className="text-center mb-12">
          <p className="text-sm text-gray-500 uppercase tracking-wider mb-6">
            Trusted by 200+ users
          </p>

          {/* Carousel Container */}
          <div className="relative max-w-6xl mx-auto">
            {/* Previous Button */}
            <button
              onClick={prevSlide}
              disabled={currentIndex === 0}
              className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg transition-all duration-200 ${currentIndex === 0
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-emerald-50 hover:scale-110'
                }`}
              aria-label="Previous testimonials"
            >
              <ChevronLeft className="w-6 h-6 text-emerald-600" />
            </button>

            {/* Testimonials Grid */}
            <div className="overflow-hidden">
              <div
                className="transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(-${currentIndex * 100}%)`,
                }}
              >
                <div className="flex">
                  {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                    <div
                      key={slideIndex}
                      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 min-w-full"
                    >
                      {testimonials
                        .slice(
                          slideIndex * testimonialsPerView,
                          slideIndex * testimonialsPerView + testimonialsPerView
                        )
                        .map((testimonial, index) => {
                          const actualIndex = slideIndex * testimonialsPerView + index;
                          return (
                            <div
                              key={actualIndex}
                              className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300"
                            >
                              <div className="flex items-center gap-3 mb-4">
                                {testimonial.avatar ? (
                                  <img
                                    src={testimonial.avatar}
                                    alt={testimonial.name}
                                    className="w-12 h-12 rounded-full object-cover border-2 border-emerald-100"
                                  />
                                ) : (
                                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                                    {testimonial.name[0]}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 truncate">
                                    {testimonial.name}
                                  </p>
                                  <div className="flex items-center gap-1">
                                    {[...Array(5)].map((_, i) => (
                                      <Star
                                        key={i}
                                        className="w-3.5 h-3.5 text-yellow-400 fill-current"
                                      />
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Testimonial Image if exists */}
                              {testimonial.image && (
                                <img
                                  src={testimonial.image}
                                  alt={`${testimonial.name}'s review`}
                                  className="w-full h-auto rounded-lg mb-4 border border-gray-200"
                                />
                              )}

                              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                                &quot;
                                {expandedIndex === actualIndex ||
                                  testimonial.quote.length <= 150
                                  ? testimonial.quote
                                  : `${testimonial.quote.slice(0, 150)}...`}
                                &quot;
                                {testimonial.quote.length > 150 && (
                                  <button
                                    onClick={() => toggleExpand(actualIndex)}
                                    className="ml-2 text-emerald-600 hover:text-emerald-700 font-medium text-xs"
                                  >
                                    {expandedIndex === actualIndex
                                      ? 'Show less'
                                      : 'Read more'}
                                  </button>
                                )}
                              </p>
                              {testimonial.sourceUrl ? (
                                <a
                                  href={testimonial.sourceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-emerald-600 hover:text-emerald-700 transition-colors font-medium"
                                >
                                  Via {testimonial.source}
                                </a>
                              ) : (
                                <span className="text-xs text-gray-500 font-medium">
                                  Via {testimonial.source}
                                </span>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Next Button */}
            <button
              onClick={nextSlide}
              disabled={currentIndex === maxIndex}
              className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg transition-all duration-200 ${currentIndex === maxIndex
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-emerald-50 hover:scale-110'
                }`}
              aria-label="Next testimonials"
            >
              <ChevronRight className="w-6 h-6 text-emerald-600" />
            </button>
          </div>

          {/* Pagination Dots */}
          <div className="flex justify-center items-center gap-2 mt-8">
            {Array.from({ length: maxIndex + 1 }).map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (!isAnimating) {
                    setIsAnimating(true);
                    setCurrentIndex(index);
                    setTimeout(() => setIsAnimating(false), 500);
                  }
                }}
                className={`h-2 rounded-full transition-all duration-300 ${currentIndex === index
                    ? 'w-8 bg-emerald-600'
                    : 'w-2 bg-gray-300 hover:bg-gray-400'
                  }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col justify-center items-center mt-12">
          <h3 className="text-2xl font-bold text-gray-900">
            Recognized as a Top Choice by Developers and Startups
          </h3>
          <div className="flex justify-center items-center gap-4 mt-8">
            <a
              href="https://www.uneed.best/tool/seozast"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://www.uneed.best/POTD1.png"
                alt="SEOZast Uneed Product of the Day"
                className="h-10 sm:h-12 w-auto rounded"
              />
            </a>
            <a
              href="https://fazier.com/launches/seozast"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0"
            >
              <img
                src="https://fazier.com/api/v1/public/badges/embed_image.svg?launch_id=4694&badge_type=weekly&theme=light"
                alt="Fazier First Place Badge"
                className="h-10 sm:h-12 w-auto"
              />
            </a>
            <a
              href="https://tinylaun.ch"
              target="_blank"
              rel="noopener"
              className="flex-shrink-0"
            >
              <img
                src="https://tinylaun.ch/tinylaunch_badge_2.svg"
                alt="TinyLaunch Badge"
                className="h-10 sm:h-12 w-auto"
              />
            </a>
          </div>
        </div>
      </Container>

      {/* Review Schema for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'SEOZast',
            url: 'https://seozast.com',
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: '5',
              reviewCount: testimonials.length,
            },
            review: testimonials.map((testimonial) => ({
              '@type': 'Review',
              author: {
                '@type': 'Person',
                name: testimonial.name,
              },
              datePublished: testimonial.datePublished,
              reviewBody: testimonial.quote,
              reviewRating: {
                '@type': 'Rating',
                ratingValue: 5,
              },
              publisher: testimonial.sourceUrl
                ? {
                  '@type': 'Organization',
                  name: testimonial.source,
                  url:
                    testimonial.source === 'Product Hunt'
                      ? 'https://www.producthunt.com'
                      : testimonial.source === 'Fazier'
                        ? 'https://fazier.com'
                        : testimonial.source === 'X (Twitter)'
                          ? 'https://x.com'
                          : 'https://www.uneed.best',
                }
                : undefined,
            })),
          }),
        }}
      />
    </section>
  );
};

export default Testimonials;

// const Testimonials = () => {
//   const [currentIndex, setCurrentIndex] = React.useState(0);
//   const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null);
//   const [isAnimating, setIsAnimating] = React.useState(false);

//   const testimonialsPerView = 3;
//   const totalSlides = Math.ceil(testimonials.length / testimonialsPerView);
//   const maxIndex = totalSlides - 1;

//   const nextSlide = () => {
//     if (!isAnimating && currentIndex < maxIndex) {
//       setIsAnimating(true);
//       setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
//       setTimeout(() => setIsAnimating(false), 500);
//     }
//   };

//   const prevSlide = () => {
//     if (!isAnimating && currentIndex > 0) {
//       setIsAnimating(true);
//       setCurrentIndex((prev) => Math.max(prev - 1, 0));
//       setTimeout(() => setIsAnimating(false), 500);
//     }
//   };

//   const toggleExpand = (index: number) => {
//     setExpandedIndex(expandedIndex === index ? null : index);
//   };

//   React.useEffect(() => {
//     const handleKeyDown = (e: KeyboardEvent) => {
//       if (e.key === 'ArrowLeft') prevSlide();
//       if (e.key === 'ArrowRight') nextSlide();
//     };
//     window.addEventListener('keydown', handleKeyDown);
//     return () => window.removeEventListener('keydown', handleKeyDown);
//   }, [currentIndex, isAnimating]);

//   return (
//     <section className="bg-gray-50 py-16 border-b border-gray-200">
//       <Container>
//         <div className="text-center mb-12">
//           <p className="text-sm text-gray-500 uppercase tracking-wider mb-6">
//             Trusted by 200+ users
//           </p>

//           {/* Carousel Container */}
//           <div className="relative max-w-6xl mx-auto">
//             {/* Previous Button */}
//             <button
//               onClick={prevSlide}
//               disabled={currentIndex === 0}
//               className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg transition-all duration-200 ${currentIndex === 0
//                   ? 'opacity-50 cursor-not-allowed'
//                   : 'hover:bg-emerald-50 hover:scale-110'
//                 }`}
//               aria-label="Previous testimonials"
//             >
//               <ChevronLeft className="w-6 h-6 text-emerald-600" />
//             </button>

//             {/* Testimonials Grid */}
//             <div className="overflow-hidden">
//               <div
//                 className="transition-transform duration-500 ease-in-out"
//                 style={{
//                   transform: `translateX(-${currentIndex * 100}%)`,
//                 }}
//               >
//                 <div className="flex">
//                   {Array.from({ length: totalSlides }).map((_, slideIndex) => (
//                     <div
//                       key={slideIndex}
//                       className="grid md:grid-cols-3 gap-8 min-w-full"
//                     >
//                       {testimonials
//                         .slice(
//                           slideIndex * testimonialsPerView,
//                           slideIndex * testimonialsPerView + testimonialsPerView
//                         )
//                         .map((testimonial, index) => {
//                           const actualIndex = slideIndex * testimonialsPerView + index;
//                           return (
//                             <div
//                               key={actualIndex}
//                               className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300"
//                             >
//                               <div className="flex items-center gap-3 mb-4">
//                                 {testimonial.avatar ? (
//                                   <img
//                                     src={testimonial.avatar}
//                                     alt={testimonial.name}
//                                     className="w-12 h-12 rounded-full object-cover border-2 border-emerald-100"
//                                   />
//                                 ) : (
//                                   <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
//                                     {testimonial.name[0]}
//                                   </div>
//                                 )}
//                                 <div className="flex-1 min-w-0">
//                                   <p className="font-semibold text-gray-900 truncate">
//                                     {testimonial.name}
//                                   </p>
//                                   <div className="flex items-center gap-1">
//                                     {[...Array(5)].map((_, i) => (
//                                       <Star
//                                         key={i}
//                                         className="w-3.5 h-3.5 text-yellow-400 fill-current"
//                                       />
//                                     ))}
//                                   </div>
//                                 </div>
//                               </div>

//                               {/* Testimonial Image if exists */}
//                               {testimonial.image && (
//                                 <img
//                                   src={testimonial.image}
//                                   alt={`${testimonial.name}'s review`}
//                                   className="w-full h-auto rounded-lg mb-4 border border-gray-200"
//                                 />
//                               )}

//                               <p className="text-gray-700 mb-4 text-sm leading-relaxed">
//                                 &quot;
//                                 {expandedIndex === actualIndex ||
//                                   testimonial.quote.length <= 150
//                                   ? testimonial.quote
//                                   : `${testimonial.quote.slice(0, 150)}...`}
//                                 &quot;
//                                 {testimonial.quote.length > 150 && (
//                                   <button
//                                     onClick={() => toggleExpand(actualIndex)}
//                                     className="ml-2 text-emerald-600 hover:text-emerald-700 font-medium text-xs"
//                                   >
//                                     {expandedIndex === actualIndex
//                                       ? 'Show less'
//                                       : 'Read more'}
//                                   </button>
//                                 )}
//                               </p>
//                               {testimonial.sourceUrl ? (
//                                 <a
//                                   href={testimonial.sourceUrl}
//                                   target="_blank"
//                                   rel="noopener noreferrer"
//                                   className="text-xs text-emerald-600 hover:text-emerald-700 transition-colors font-medium"
//                                 >
//                                   Via {testimonial.source}
//                                 </a>
//                               ) : (
//                                 <span className="text-xs text-gray-500 font-medium">
//                                   Via {testimonial.source}
//                                 </span>
//                               )}
//                             </div>
//                           );
//                         })}
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             </div>

//             {/* Next Button */}
//             <button
//               onClick={nextSlide}
//               disabled={currentIndex === maxIndex}
//               className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg transition-all duration-200 ${currentIndex === maxIndex
//                   ? 'opacity-50 cursor-not-allowed'
//                   : 'hover:bg-emerald-50 hover:scale-110'
//                 }`}
//               aria-label="Next testimonials"
//             >
//               <ChevronRight className="w-6 h-6 text-emerald-600" />
//             </button>
//           </div>

//           {/* Pagination Dots */}
//           <div className="flex justify-center items-center gap-2 mt-8">
//             {Array.from({ length: maxIndex + 1 }).map((_, index) => (
//               <button
//                 key={index}
//                 onClick={() => {
//                   if (!isAnimating) {
//                     setIsAnimating(true);
//                     setCurrentIndex(index);
//                     setTimeout(() => setIsAnimating(false), 500);
//                   }
//                 }}
//                 className={`h-2 rounded-full transition-all duration-300 ${currentIndex === index
//                     ? 'w-8 bg-emerald-600'
//                     : 'w-2 bg-gray-300 hover:bg-gray-400'
//                   }`}
//                 aria-label={`Go to slide ${index + 1}`}
//               />
//             ))}
//           </div>
//         </div>

//         <div className="flex flex-col justify-center items-center mt-12">
//           <h3 className="text-2xl font-bold text-gray-900">
//             Recognized as a Top Choice by Developers and Startups
//           </h3>
//           <div className="flex justify-center items-center gap-4 mt-8">
//             <a
//               href="https://www.uneed.best/tool/seozast"
//               target="_blank"
//               rel="noopener noreferrer"
//             >
//               <img
//                 src="https://www.uneed.best/POTD1.png"
//                 alt="SEOZast Uneed Product of the Day"
//                 className="h-10 sm:h-12 w-auto rounded"
//               />
//             </a>
//             <a
//               href="https://fazier.com/launches/seozast"
//               target="_blank"
//               rel="noopener noreferrer"
//               className="flex-shrink-0"
//             >
//               <img
//                 src="https://fazier.com/api/v1/public/badges/embed_image.svg?launch_id=4694&badge_type=weekly&theme=light"
//                 alt="Fazier First Place Badge"
//                 className="h-10 sm:h-12 w-auto"
//               />
//             </a>
//             <a
//               href="https://tinylaun.ch"
//               target="_blank"
//               rel="noopener"
//               className="flex-shrink-0"
//             >
//               <img
//                 src="https://tinylaun.ch/tinylaunch_badge_2.svg"
//                 alt="TinyLaunch Badge"
//                 className="h-10 sm:h-12 w-auto"
//               />
//             </a>
//           </div>
//         </div>
//       </Container>

//       {/* Review Schema for SEO */}
//       <script
//         type="application/ld+json"
//         dangerouslySetInnerHTML={{
//           __html: JSON.stringify({
//             '@context': 'https://schema.org',
//             '@type': 'SoftwareApplication',
//             name: 'SEOZast',
//             url: 'https://seozast.com',
//             aggregateRating: {
//               '@type': 'AggregateRating',
//               ratingValue: '5',
//               reviewCount: testimonials.length,
//             },
//             review: testimonials.map((testimonial) => ({
//               '@type': 'Review',
//               author: {
//                 '@type': 'Person',
//                 name: testimonial.name,
//               },
//               datePublished: testimonial.datePublished,
//               reviewBody: testimonial.quote,
//               reviewRating: {
//                 '@type': 'Rating',
//                 ratingValue: 5,
//               },
//               publisher: testimonial.sourceUrl
//                 ? {
//                   '@type': 'Organization',
//                   name: testimonial.source,
//                   url:
//                     testimonial.source === 'Product Hunt'
//                       ? 'https://www.producthunt.com'
//                       : testimonial.source === 'Fazier'
//                         ? 'https://fazier.com'
//                         : testimonial.source === 'X (Twitter)'
//                           ? 'https://x.com'
//                           : 'https://www.uneed.best',
//                 }
//                 : undefined,
//             })),
//           }),
//         }}
//       />
//     </section>
//   );
// };

// export default Testimonials;